/**
 * Balance Context
 *
 * Shared Privy wallet balance state with 10-second polling via GET /api/auth/balances.
 * All pages use this instead of independently fetching balances.
 * Evore subaccount balances are fetched separately by MiningDashboard.
 */

import {
  ReactNode,
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { usePrivy } from '@privy-io/react-auth';
import { authAPI } from '../api/client';

const POLL_INTERVAL_MS = 10_000; // 10 seconds

export interface BalanceContextType {
  // Privy wallet balances
  solBalance: number;
  oreBalance: number;
  storeBalance: number;
  usdcBalance: number;
  flp8Balance: number;
  claimableOre: number;
  claimableSol: number;

  // Mining status (cheap in-memory check on server)
  isMining: boolean;

  // Meta
  isLoading: boolean;
  lastUpdated: number | null;

  // Imperative refresh (e.g., after a transaction)
  refreshBalances: () => Promise<void>;
}

const BalanceContext = createContext<BalanceContextType>({
  solBalance: 0,
  oreBalance: 0,
  storeBalance: 0,
  usdcBalance: 0,
  flp8Balance: 0,
  claimableOre: 0,
  claimableSol: 0,
  isMining: false,
  isLoading: false,
  lastUpdated: null,
  refreshBalances: async () => {},
});

export function useBalances() {
  return useContext(BalanceContext);
}

interface BalanceProviderProps {
  children: ReactNode;
}

export function BalanceProvider({ children }: BalanceProviderProps) {
  const { authenticated, getAccessToken } = usePrivy();

  // Balance state
  const [solBalance, setSolBalance] = useState(0);
  const [oreBalance, setOreBalance] = useState(0);
  const [storeBalance, setStoreBalance] = useState(0);
  const [usdcBalance, setUsdcBalance] = useState(0);
  const [flp8Balance, setFlp8Balance] = useState(0);
  const [claimableOre, setClaimableOre] = useState(0);
  const [claimableSol, setClaimableSol] = useState(0);
  const [isMining, setIsMining] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<number | null>(null);

  // Refs for deduplication and interval management
  const fetchInProgressRef = useRef(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Stable ref for getAccessToken — Privy changes this reference on token
  // refresh (~every 15 min), which would otherwise tear down and recreate
  // the 10s polling interval, causing cascading re-renders across all pages.
  const getAccessTokenRef = useRef(getAccessToken);
  getAccessTokenRef.current = getAccessToken;

  const fetchBalances = useCallback(async () => {
    if (!authenticated || fetchInProgressRef.current) return;
    fetchInProgressRef.current = true;

    try {
      const token = await getAccessTokenRef.current();
      if (!token) return;

      const data = await authAPI.getBalances(token);

      if (data.privy) {
        setSolBalance(data.privy.solBalance);
        setOreBalance(data.privy.oreBalance);
        setStoreBalance(data.privy.storeBalance);
        setUsdcBalance(data.privy.usdcBalance);
        setFlp8Balance(data.privy.flp8Balance);
        setClaimableOre(data.privy.claimableOre);
        setClaimableSol(data.privy.claimableSol);
      }

      setIsMining(data.isMining);
      setLastUpdated(Date.now());
    } catch (err) {
      // Silent fail — balance polling should not disrupt the UI
    } finally {
      fetchInProgressRef.current = false;
      setIsLoading(false);
    }
  }, [authenticated]);

  // Imperative refresh: immediate fetch + reset interval
  const refreshBalances = useCallback(async () => {
    // Clear existing interval so the next auto-poll is 10s from now
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    await fetchBalances();

    // Restart interval
    if (authenticated) {
      intervalRef.current = setInterval(fetchBalances, POLL_INTERVAL_MS);
    }
  }, [fetchBalances, authenticated]);

  // Start/stop polling based on auth state
  useEffect(() => {
    if (!authenticated) {
      // Clear state on logout
      setSolBalance(0);
      setOreBalance(0);
      setStoreBalance(0);
      setUsdcBalance(0);
      setFlp8Balance(0);
      setClaimableOre(0);
      setClaimableSol(0);
      setIsMining(false);
      setLastUpdated(null);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    // Initial fetch
    setIsLoading(true);
    fetchBalances();

    // Start polling
    intervalRef.current = setInterval(fetchBalances, POLL_INTERVAL_MS);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [authenticated, fetchBalances]);

  return (
    <BalanceContext.Provider
      value={{
        solBalance,
        oreBalance,
        storeBalance,
        usdcBalance,
        flp8Balance,
        claimableOre,
        claimableSol,
        isMining,
        isLoading,
        lastUpdated,
        refreshBalances,
      }}
    >
      {children}
    </BalanceContext.Provider>
  );
}
